import { Box, Typography } from '@mui/material'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import { PreviewTableHeader } from 'module/payroll/resources/PreviewTableHeader'
import React, { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { storeEmployeeDetails } from 'services/api/modules/payroll/payroll-config/bulkImportApi'
import PreviewTable from './PreviewTable'

const PreviewPayrollImport = ({ state }) => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const [pageState, setPageState] = useState('IMPORT')

  const columnHeader = useMemo(() => PreviewTableHeader, [PreviewTableHeader])

  let staffDetails = state?.staffData?.data
  const { mutate } = useMutation(storeEmployeeDetails, {
    onSuccess: (response) => {
      queryClient.invalidateQueries('staffListData')
      setPageState('SUCCESSFUL')
    },
    onError: (error) => {
      if (error.status === 422) {
        console.log(error)
      } else {
        console.log(error)
      }
    },
  })

  const handleCloseModal = (event, reason) => {
    if (reason && reason === 'backdropClick') {
      return
    }
    navigate(-1)
  }

  if (pageState === 'SUCCESSFUL') {
    return (
      <AdxInfoModal
        open={true}
        muiIcon="insert_drive_file"
        iconColor="alternate.main"
        title={`Employee details updated!`}
        subtitle={`The employee details have been updated. You may proceed to run the payroll`}
        modalWidth="500px"
        footerButtons
        firstBtnLabel={'Close'}
        firstBtnHandle={() => navigate('/app/management-centre/company-management/payroll')}
      ></AdxInfoModal>
    )
  }

  const DataChip = ({ color, content, sx, ...rest }) => {
    return (
      <Box
        sx={{
          width: '170px',
          height: '34px',
          backgroundColor: 'gray-background.main',
          display: 'flex',
          alignItems: 'center',
          px: 1,
          ...sx,
        }}
        {...rest}
      >
        <Box
          sx={{
            width: '4px',
            height: '80%',
            backgroundColor: color,
            mr: 1,
          }}
        />
        <Typography variant="body1" color={color}>
          {content}
        </Typography>
      </Box>
    )
  }

  const handleReuploadCSV = () => {
    navigate(-1)
  }

  const handleSubmissionDetails = () => {
    if (staffDetails.errors_count === 0) {
      mutate({ staff: staffDetails?.staff })
    } else {
      alert('There are some errors. Please fill in the file accordingly')
    }
  }

  return (
    <AdxFormModal
      open={true}
      onClose={handleCloseModal}
      handleClose={handleCloseModal}
      disableEscapeKeyDown
      disableAutoFocus
      sxModal={{
        minWidth: { xs: 'none', sm: 'calc(100vw - 30px)', lg: '1000px' },
        width: { xs: 'calc(100vw - 30px)', lg: 'calc(100vw - 1500px)' },
      }}
      title={'Import payroll details'}
      childPadding={false}
      enableFooter
      actionButtonDisabled={staffDetails && staffDetails.errors_count !== 0 ? true : false}
      actionText="Import staff details"
      handleAction={handleSubmissionDetails}
      cancelText="Re-upload CSV"
    >
      <>
        {pageState === 'IMPORT' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              px: 3,
              py: 2,
            }}
          >
            <Box
              sx={{
                width: 1,
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="body2" color="dark-gray-text.main">
                  Step 3
                </Typography>

                <Typography mr={1} variant="body2" color="light-gray-text.main">
                  Check the details uploaded. Then, proceed to save the latest details of employees.
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'flex-start',
                  gap: 1,
                  width: { xs: 1, md: 'fit-content' },
                }}
              >
                <DataChip
                  color="info.main"
                  content={`${staffDetails && staffDetails.staff_count} data found`}
                  sx={{ width: { xs: 1, md: 'fit-content' } }}
                />
                <DataChip
                  color="error.main"
                  content={`${staffDetails && staffDetails.errors_count} ${
                    staffDetails && staffDetails.errors_count > 1 ? 'errors' : 'error'
                  } found`}
                  sx={{ width: { xs: 1, md: 'fit-content' } }}
                />
              </Box>
            </Box>
            <Box sx={{ border: 1, borderColor: 'gray-line.main', borderRadius: '4.5px' }}>
              <Box
                sx={{
                  width: '100%',
                  height: '45px',
                  backgroundColor: 'gray-background.main',
                  display: 'flex',
                  py: 1,
                  px: 3,
                  alignItems: 'center',
                  borderBottom: 1,
                  borderColor: 'gray-line.main',
                }}
              >
                <Box
                  sx={{
                    minWidth: '150px',
                  }}
                  width={1 / 6}
                >
                  <Typography variant="body1" color="dark-gray-text.main">
                    ID
                  </Typography>
                </Box>
                <Box sx={{ minWidth: '150px' }} width={1 / 6}>
                  <Typography variant="body1" color="dark-gray-text.main">
                    Staff Name
                  </Typography>
                </Box>
                <Box sx={{ minWidth: '150px', pl: 4 }} width={1}>
                  <Typography variant="body1" color="dark-gray-text.main">
                    Remarks
                  </Typography>
                </Box>
              </Box>
              <PreviewTable staffData={staffDetails} />
            </Box>
          </Box>
        )}
      </>
    </AdxFormModal>
  )
}

export default PreviewPayrollImport
